/* eslint-disable import/no-cycle */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Image, NumberFormatter, Skeleton } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { Shirt } from 'tabler-icons-react';
import AppStack from '../../../../../common/AppStack';
import AppText from '../../../../../common/AppText';
import AppFlexbox from '../../../../../common/AppFlexbox';
import WsColorSwatch from '../../blocks/variantPicker/WsColorSwatch';
import { getVariantOptions } from '../../../../../../helpers/shop/webstoreHelper';
import { generateColorVariants } from '../../../../../../helpers/styles';
import { Context as HelperContext } from '../../../../../../providers/HelperContextProvider';
import { triggerNotification } from '../../../../../../helpers/notificationHelper';
import { useRegCartController } from '../../../../../../helpers/shop/useRegCartController';

const WsProductCard = ({
  ecomStoreProduct,
  showSecondImageOnHover,
  colorScheme,
  showVendorName,
  portraitRatio,
  isQuickAddEnabled,
  hideAllProductBadges,
  hideFreeShippingBadge,
  url,
  baseStoreUrl,
  onNavigate,
  noProduct,
  analyticCta
}) => {
  const { addCartProducts } = useRegCartController();
  const { toggleWebstoreProductsShowcaseModal } = useContext(HelperContext);
  const { hovered, ref: hoverRef } = useHover();
  const [loading, setLoading] = useState(false);
  const { ecomVendorProduct } = ecomStoreProduct ?? {};
  const images = ecomVendorProduct
    ? ecomVendorProduct.ecomVendorBundle &&
      ecomVendorProduct.previewImages.length === 0 &&
      ecomVendorProduct.ecomVendorProductMedia.length === 0
      ? ecomVendorProduct.ecomVendorBundle.ecomVendorBundleProducts
          .map((p) => p.previewImage)
          .filter(Boolean)
      : ecomVendorProduct.previewImages ??
        ecomVendorProduct.ecomVendorProductMedia
          .sort((a, b) => a.sort - b.sort)
          .map((m) => m.src)
    : [];

  ecomVendorProduct?.previewImages ??
    ecomVendorProduct?.ecomVendorProductMedia
      .sort((a, b) => a.sort - b.sort)
      .map((m) => m.src) ??
    [];
  const secondImage = images[1];
  const showSecondImage = !!secondImage && hovered && showSecondImageOnHover;
  const lowestPriceVariant = ecomStoreProduct?.ecomStoreProductVariants.reduce(
    (acc, variant) => {
      if (variant.price < acc.price) {
        return variant;
      }
      return acc;
    }
  );
  const highestPriceVariant = ecomStoreProduct?.ecomStoreProductVariants.reduce(
    (acc, variant) => {
      if (variant.price > acc.price) {
        return variant;
      }
      return acc;
    }
  );

  const hasAvailableQuantity = ecomStoreProduct?.ecomStoreProductVariants.some(
    (variant) =>
      variant.ecomVendorProductVariant.quantity == null ||
      variant.ecomVendorProductVariant.quantity > 0
  );
  const textColorVariants = generateColorVariants(colorScheme.textColor, 100);

  const productUnavailable =
    ecomStoreProduct?.ecomVendorProduct.deleted ||
    ecomStoreProduct?.deleted ||
    ecomStoreProduct?.ecomStoreProductVariants.every(
      (v) => v.deleted || v.ecomVendorProductVariant.deleted || v.unavailable
    );

  const hasGoalSet = ecomStoreProduct?.fundraisingQuantityGoal > 0;

  const allVariantsHaveColorsSet = ecomStoreProduct?.ecomStoreProductVariants.every(
    (variant) => !!variant.ecomVendorProductVariant.primaryColor
  );
  const variantOptions = getVariantOptions(ecomStoreProduct);
  const colorOption = variantOptions.find(
    (op) => op.name.toLowerCase() === 'color'
  );
  const showColorSwatches = allVariantsHaveColorsSet && !!colorOption;

  const isExternalProduct =
    ecomVendorProduct &&
    (ecomVendorProduct.ecomVendorProductExternalIds.length > 0 ||
      ecomVendorProduct.ecomVendorBundle?.ecomVendorBundleProducts.some(
        (p) => p.ecomVendorProductExternalIds.length > 0
      ));

  const onAddProductToCart = () => {
    const submitData = {
      pkEcomStore: ecomStoreProduct.fkEcomStore,
      ecomStoreCartProducts: [
        {
          ecomStoreProduct,
          uuid:
            ecomStoreProduct.ecomStoreProductVariants[0]
              .ecomVendorProductVariant.uuid,
          count: 1,
          analytics: { ctaCaptured: analyticCta }
        }
      ]
    };

    setLoading(true);
    addCartProducts(
      submitData,
      () => {
        setLoading(false);
        triggerNotification('Product added to cart', 'Success', 'green');
        toggleWebstoreProductsShowcaseModal({
          isOpen: true,
          showCheckoutSummary: true
        });
      },
      (message) => {
        setLoading(false);
        if (message.toLowerCase().includes('clear your cart')) {
          toggleWebstoreProductsShowcaseModal({
            isOpen: true,
            showClearCart: true,
            resubmitState: submitData
          });
        }
        else {
          triggerNotification(message);
        }
      }
    );
  };

  return (
    <AppStack
      ref={hoverRef}
      component={ecomStoreProduct ? Link : null}
      onClick={ecomStoreProduct ? (e) => onNavigate(e, ecomStoreProduct) : null}
      style={{
        position: 'relative',
        gap: hasGoalSet ? 5 : 10,
        flex: 1,
        padding: 0,
        height: '100%',
        width: '100%',
        cursor: ecomStoreProduct
          ? 'pointer'
          : noProduct
          ? 'not-allowed'
          : 'unset',
        textDecoration: 'none',
        color: colorScheme.textColor
      }}
      to={
        url ||
        (ecomStoreProduct
          ? `${baseStoreUrl}/products/${ecomStoreProduct?.urlHandle}`
          : null)
      }
    >
      {ecomStoreProduct ? (
        <>
          <AppStack
            style={{
              border: !images[0] ? 'solid 1px lightgrey' : 'none',
              position: 'relative',
              width: '100%',
              height: 200,
              backgroundColor: !images[0] ? '#f2f2f2' : 'unset',
              overflow: 'hidden',
              aspectRatio: portraitRatio ? '3 / 4' : '1 / 1'
            }}
          >
            <Image
              fit="contain"
              h="100%"
              loading="lazy"
              src={images[0]}
              style={{
                opacity: showSecondImage ? 0 : 1,
                transition: 'opacity 0.4s ease-in-out',
                position: 'absolute'
              }}
              w="100%"
            />
            <Image
              fit="contain"
              h="100%"
              loading="lazy"
              src={secondImage}
              style={{
                opacity: showSecondImage ? 1 : 0,
                transition: 'opacity 0.4s ease-in-out',
                position: 'absolute'
              }}
              w="100%"
            />

            {!isExternalProduct &&
              !hideAllProductBadges &&
              !hideFreeShippingBadge && (
                <Badge
                  color="green"
                  size="md"
                  style={{ position: 'absolute', right: 8, top: 8 }}
                  variant="filled"
                >
                  Free Shipping
                </Badge>
              )}
          </AppStack>

          {/* {hasGoalSet && (
            <AppFlexbox style={{ alignItems: 'center', gap: 8 }}>
              <AppText
                style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}
              >
                40% claimed
              </AppText>
              <AppFlexbox style={{ flex: 1, alignItems: 'center' }}>
                <Progress radius={0} style={{ width: '100%' }} value={40} />
              </AppFlexbox>
            </AppFlexbox>
          )} */}
          <AppStack style={{ flex: 1, gap: 0 }}>
            <AppStack style={{ flex: 1, gap: 0 }}>
              <AppText
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  textDecoration: hovered ? 'underline' : 'unset'
                }}
              >
                {ecomVendorProduct.name}
              </AppText>{' '}
              {showVendorName && (
                <AppText
                  style={{
                    fontSize: 12,
                    color: textColorVariants.lighter
                  }}
                >
                  {ecomVendorProduct.vendorName}
                </AppText>
              )}
              {showColorSwatches && (
                <AppStack style={{ flex: 1, gap: 0 }}>
                  <AppFlexbox style={{ gap: 0 }}>
                    {colorOption.values.map((v) => {
                      const colorVariant = ecomStoreProduct?.ecomStoreProductVariants.find(
                        (f) =>
                          f.ecomVendorProductVariant.ecomVendorProductVariantOptions.some(
                            (so) =>
                              so.nameSort === colorOption.sort &&
                              so.name === colorOption.name &&
                              so.value === v.value
                          )
                      );

                      return (
                        <WsColorSwatch
                          key={v.value}
                          primaryColor={
                            colorVariant.ecomVendorProductVariant.primaryColor
                          }
                          secondaryColor={
                            colorVariant.ecomVendorProductVariant.secondaryColor
                          }
                          size={16}
                          value={v.value}
                        />
                      );
                    })}
                  </AppFlexbox>
                </AppStack>
              )}
            </AppStack>

            <AppFlexbox style={{ gap: 12, alignItems: 'end' }}>
              {lowestPriceVariant.compareAtPrice > 0 && (
                <AppText
                  style={{
                    fontSize: 16,
                    fontWeight: 450,
                    color: textColorVariants.lighter,
                    textDecoration: 'line-through'
                  }}
                >
                  <NumberFormatter
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    suffix=" CAD"
                    thousandSeparator
                    value={lowestPriceVariant.compareAtPrice / 100}
                  />
                </AppText>
              )}

              <AppText style={{ fontSize: 16, fontWeight: 450 }}>
                {lowestPriceVariant.price !== highestPriceVariant.price
                  ? 'From '
                  : ''}
                <NumberFormatter
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="$"
                  suffix=" CAD"
                  thousandSeparator
                  value={lowestPriceVariant.price / 100}
                />
              </AppText>
            </AppFlexbox>
          </AppStack>
          {isQuickAddEnabled && (
            <AppStack style={{ width: '100%', paddingBottom: 2 }}>
              <Button
                color={colorScheme.outlineButtonColor}
                disabled={!hasAvailableQuantity || productUnavailable}
                loading={loading}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (
                    ecomStoreProduct.ecomStoreProductVariants.length > 1 ||
                    ecomStoreProduct.ecomVendorProductInputCount > 0
                  ) {
                    toggleWebstoreProductsShowcaseModal({
                      isOpen: true,
                      ecomStoreProduct,
                      analyticCta
                    });
                  }
                  else {
                    onAddProductToCart();
                  }
                }}
                radius={0}
                size="md"
                style={{ width: '100%' }}
                styles={{ label: { textDecoration: 'none' } }}
                variant="outline"
              >
                {productUnavailable
                  ? 'Unavailable'
                  : !hasAvailableQuantity
                  ? 'Sold out'
                  : ecomStoreProduct.ecomStoreProductVariants.length > 1 ||
                    ecomStoreProduct.ecomVendorProductInputCount > 0
                  ? 'Choose options'
                  : 'Add to cart'}
              </Button>
            </AppStack>
          )}
        </>
      ) : noProduct ? (
        <>
          <AppStack
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              backgroundColor: '#f2f2f2',
              overflow: 'hidden',
              aspectRatio: portraitRatio ? '3 / 4' : '1 / 1',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Shirt color="#444" size="65%" />
          </AppStack>
          <AppStack style={{ flex: 1, gap: 0 }}>
            <AppStack style={{ flex: 1, gap: 0 }}>
              <AppText
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  textDecoration: hovered ? 'underline' : 'unset'
                }}
              >
                Product Name
              </AppText>
            </AppStack>

            <AppFlexbox style={{ gap: 12, alignItems: 'end' }}>
              <AppText
                style={{
                  fontSize: 16,
                  fontWeight: 450
                }}
              >
                <NumberFormatter
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="$"
                  suffix=" CAD"
                  thousandSeparator
                  value={0}
                />
              </AppText>
            </AppFlexbox>
          </AppStack>
        </>
      ) : (
        <>
          <AppStack
            style={{
              position: 'relative',
              width: '100%',
              height: 200,
              backgroundColor: '#f2f2f2',
              overflow: 'hidden',
              aspectRatio: portraitRatio ? '3 / 4' : '1 / 1'
            }}
          >
            <Skeleton height="100%" width="100%" />
          </AppStack>
          <AppStack style={{ gap: 5 }}>
            <Skeleton height={18} width="70%" />
            {showVendorName && <Skeleton height={16} width="45%" />}
            <Skeleton height={18} width="35%" />
          </AppStack>
          {isQuickAddEnabled && <Skeleton height={42} width="100%" />}
        </>
      )}
    </AppStack>
  );
};

WsProductCard.propTypes = {
  analyticCta: PropTypes.string,
  baseStoreUrl: PropTypes.string,
  colorScheme: PropTypes.object,
  ecomStoreProduct: PropTypes.object,
  hideAllProductBadges: PropTypes.bool,
  hideFreeShippingBadge: PropTypes.bool,
  isQuickAddEnabled: PropTypes.bool,
  noProduct: PropTypes.bool,
  onNavigate: PropTypes.func,
  portraitRatio: PropTypes.bool,
  productNotFound: PropTypes.bool,
  showSecondImageOnHover: PropTypes.bool,
  showVendorName: PropTypes.bool,
  url: PropTypes.string
};

export default WsProductCard;
